import type { Diagnostic } from '@/graphql/message-templates';

import styles from './DiagnosticList.module.css';

export interface DiagnosticListProps {
  /** Fatal: whatever was being written or rendered has no result. */
  errors?: readonly Diagnostic[];
  /** Advisory: there is a result, and something about the bag is still worth
   *  saying — an unused variable is what a typo in the body looks like from
   *  this side. */
  warnings?: readonly Diagnostic[];
}

/**
 * The `{ warnings, errors }` half of every template envelope.
 *
 * Diagnostics are payload-carried rather than transport-level, which is the
 * whole reason this is a list on the page instead of a banner: a body with
 * three missing variables is a successful response describing three specific
 * problems, each naming the path it is about, and all three arrive in one
 * round trip so the reader can fix them together.
 */
export function DiagnosticList({ errors = [], warnings = [] }: DiagnosticListProps) {
  if (errors.length === 0 && warnings.length === 0) {
    return null;
  }

  return (
    <ul className={styles.list}>
      {errors.map((diagnostic, index) => (
        <Row diagnostic={diagnostic} key={`error-${index}`} tone="error" />
      ))}
      {warnings.map((diagnostic, index) => (
        <Row diagnostic={diagnostic} key={`warning-${index}`} tone="warning" />
      ))}
    </ul>
  );
}

function Row({ diagnostic, tone }: { diagnostic: Diagnostic; tone: 'error' | 'warning' }) {
  return (
    <li className={`${styles.row} ${styles[tone]}`}>
      <span className={styles.code}>{diagnostic.code}</span>
      {diagnostic.path ? <code className={styles.path}>{diagnostic.path}</code> : null}
      <span className={styles.message}>{diagnostic.message}</span>
    </li>
  );
}
